import { format } from 'date-fns';

/**
 * The scoped revenue summary (`GET /dashboard/revenue-summary`): the
 * Dashboard's and the Reports Overview's value tiles, worked out on the server
 * by the one revenue rule. The populations each scope sums are defined there,
 * in `revenue-scope.helpers.ts`; the web renders what it is sent.
 *
 * Every money field (`value`, `previousValue`, `growthPct`) is absent for a
 * reader without `view:project:value`; the counts, stages and at-risk names
 * stay.
 */
export type RevenueSummaryScope = 'dashboard' | 'book';

export interface ScopedPopulation {
  count: number;
  value?: number;
}

export interface ScopedStageRow {
  /** Null for the `Unknown` row: the projects with no stage. */
  stageId: string | null;
  name: string;
  isDone: boolean;
  count: number;
  value?: number;
  /** The stage's projects as a share of the whole book, for its bar. */
  sharePct: number;
}

export type AtRiskFlag = 'overdue' | 'ending-soon';

/** The server's completion band: low under 30%, high from 70%. */
export type CompletionBand = 'low' | 'mid' | 'high';

export interface ScopedRevenueSummary {
  scope: RevenueSummaryScope;
  delivered: ScopedPopulation & { previousValue?: number | null; growthPct?: number | null };
  inFlight: ScopedPopulation;
  pipeline: ScopedPopulation;
  book: {
    projects: number;
    delivered: number;
    completionPct: number | null;
    completionBand: CompletionBand | null;
  };
  stages: ScopedStageRow[];
  atRisk: Array<{ id: string; name: string; flags: AtRiskFlag[] }>;
}

/** The read for one scope; the dashboard scope takes the picked calendar days. */
export function scopedRevenueSummaryUrl(
  scope: RevenueSummaryScope,
  range?: { startDate: Date; endDate: Date },
): string {
  const params = new URLSearchParams({ scope });
  if (range) {
    params.set('fromDate', format(range.startDate, 'yyyy-MM-dd'));
    params.set('toDate', format(range.endDate, 'yyyy-MM-dd'));
  }
  return `/dashboard/revenue-summary?${params}`;
}
